<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <Form ref="testForm" @submit="generateRandomTest" :validation-schema="testSchema">
        <div class="row">
          <!-- Support de cours -->
          <div class="col-md-6">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Support de cours <span class="text-danger">*</span>
              </label>
              <Field name="supportDeCours" v-slot="{ field }">
                <Multiselect
                  :options="supportOptionsComputed"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-model="formData.supportDeCours"
                  v-bind="field"
                  @change="onSupportChange"
                  placeholder="Sélectionner un support"
                  :disabled="isLoadingSupports"
                />
              </Field>
              <ErrorMessage name="supportDeCours" class="text-danger" />
              <small v-if="isLoadingSupports" class="text-info">Chargement...</small>
            </div>
          </div>

          <!-- Module -->
          <div class="col-md-6">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Module <span class="text-danger">*</span>
              </label>
              <Field name="module" v-slot="{ field }">
                <Multiselect
                  :options="moduleOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-model="formData.module"
                  v-bind="field"
                  placeholder="Sélectionner un module"
                  :disabled="!formData.supportDeCours || isLoadingModules"
                />
              </Field>
              <ErrorMessage name="module" class="text-danger" />
              <small v-if="isLoadingModules" class="text-info">Chargement des modules...</small>
            </div>
          </div>
        </div>

        <div class="col-md-12 mt-5">
          <div class="d-flex align-items-center">
            <button
              type="submit"
              class="btn btn-info me-3"
              :disabled="isDisableGenerate"
              :class="{ 'cursor-not-allowed': isDisableGenerate }"
            >
              Générer le test
            </button>
            <router-link to="/liste-sessions" class="btn btn-danger">
              Annuler
            </router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'FaireTestQCM',
  components: { Form, Field, ErrorMessage, Multiselect },
  props: {
    currentSupportId: {
      type: Number,
      default: null,
    },
  },
  setup(props) {
    const router = useRouter();

    const formData = reactive({
      supportDeCours: null as number | null,
      module: null as number | null,
    });

    const supportOptions = ref<Array<{ value: number; label: string; disabled?: boolean }>>([]);
    const moduleOptions = ref<Array<{ value: number; label: string }>>([]);
    const isLoadingSupports = ref(false);
    const isLoadingModules = ref(false);

    const supportOptionsComputed = computed(() =>
      supportOptions.value.map((option) => ({
        ...option,
        disabled: option.value === props.currentSupportId,
      }))
    );

    const testSchema = Yup.object().shape({
      supportDeCours: Yup.number().required('Support de cours obligatoire').typeError('Support invalide'),
      module: Yup.number().required('Module obligatoire').typeError('Module invalide'),
    });

    const isDisableGenerate = computed(() => {
      const disabled = !formData.supportDeCours || !formData.module;
      console.log('🔘 Bouton Générer désactivé ?', disabled, '| Support:', formData.supportDeCours, '| Module:', formData.module);
      return disabled;
    });

    watch(
      [supportOptions, () => props.currentSupportId],
      ([options, currentSupportId]) => {
        if (currentSupportId && options.some((o) => o.value === currentSupportId)) {
          formData.supportDeCours = currentSupportId;
          loadModules();
        }
      }
    );

 // ==================== CHARGEMENT DES SUPPORTS (AVEC FILTRE) ====================
const loadSupports = async () => {
  console.log('📚 1. Début du chargement des supports...');
  isLoadingSupports.value = true;
  try {
    const { data } = await ApiService.get('/supports');
    console.log('📦 2. Réponse brute API /supports →', data);

    if (data?.data?.data) {
      // ✅ DÉFINIR LE TYPE EXPLICITEMENT
      const supportsAvecModules: Array<any> = []; // ou créez une interface Support
      
      for (const support of data.data.data) {
        try {
          // Vérifier si ce support a des modules
          const modulesResponse = await ApiService.get(`/supports/${support.id}/modules`);
          const modulesData = modulesResponse.data;
          
          let hasModules = false;
          if (modulesData?.code === 200 && modulesData?.data) {
            const modules = Array.isArray(modulesData.data.data) 
              ? modulesData.data.data 
              : modulesData.data;
            hasModules = Array.isArray(modules) && modules.length > 0;
          }
          
          if (hasModules) {
            supportsAvecModules.push(support);
            console.log(`✅ Support "${support.titre}" a des modules`);
          } else {
            console.log(`❌ Support "${support.titre}" n'a PAS de modules → ignoré`);
          }
        } catch (err) {
          console.warn(`⚠️ Erreur vérification modules pour support ${support.id}:`, err);
        }
      }

      supportOptions.value = supportsAvecModules.map((s: any) => ({
        value: s.id,
        label: `${s.titre} (${s.nombreHeures}h)`,
      }));
      
      console.log(`✅ 3. ${supportsAvecModules.length}/${data.data.data.length} supports avec modules →`, supportOptions.value);
      
      if (supportsAvecModules.length === 0) {
        error('Aucun support de cours avec modules disponible');
      }
    } else {
      console.warn('⚠️ Aucune donnée dans data.data.data');
    }
  } catch (err: any) {
    console.error('💥 ERREUR API /supports →', err.response?.data || err);
    error('Impossible de charger les supports');
  } finally {
    isLoadingSupports.value = false;
    console.log('🏁 Fin chargement supports');
  }
};

    // ==================== CHARGEMENT DES MODULES ====================
    const loadModules = async () => {
      const supportId = formData.supportDeCours;
      console.log('📖 4. loadModules() appelée | supportId =', supportId);

      // Réinitialisation immédiate
      moduleOptions.value = [];
      formData.module = null;
      console.log('🗑️ 5. Réinitialisation : moduleOptions & formData.module');

      if (!supportId) {
        console.log('⏹️ Aucun support → arrêt');
        return;
      }

      isLoadingModules.value = true;
      console.log('🔄 6. Appel API → GET /supports/' + supportId + '/modules');

      try {
        const { data } = await ApiService.get(`/supports/${supportId}/modules`);
        console.log('📥 7. Réponse brute modules →', data);

        if (data?.code === 200 && data?.data) {
          const modules = Array.isArray(data.data.data) ? data.data.data : data.data;
          if (modules.length > 0) {
            moduleOptions.value = modules.map((m: any) => ({
              value: m.id,
              label: m.titre || 'Module sans titre',
            }));
            console.log('✅ 8. Modules chargés →', moduleOptions.value);
          } else {
            console.log('⚠️ 8. Aucun module pour ce support');
          }
        } else {
          console.warn('⚠️ Structure inattendue ou code ≠ 200');
        }
      } catch (err: any) {
        console.error('💥 ERREUR API modules →', err.response?.data || err);
        error('Modules introuvables');
      } finally {
        isLoadingModules.value = false;
        console.log('🏁 Fin loadModules');
      }
    };

    // ==================== CHANGEMENT DE SUPPORT ====================
    const onSupportChange = (value: number) => {
      console.log('🔄 9. Support changé → ID:', value);
      formData.supportDeCours = value;
      formData.module = null;
      moduleOptions.value = [];
      console.log('🗑️ 10. Réinitialisation module avant rechargement');

      if (value) {
        console.log('⏩ Lancement loadModules()');
        loadModules();
      }
    };

    // ==================== GÉNÉRATION DU TEST ====================
    const generateRandomTest = async () => {
      console.log('🎯 11. CLIC sur Générer !');
      console.log('📦 Payload envoyé →', {
        supportDeCoursId: formData.supportDeCours,
        moduleId: formData.module,
        count: 20,
      });

      try {
        const { data } = await ApiService.post('/random-questions', {
          supportDeCoursId: formData.supportDeCours,
          moduleId: formData.module,
          count: 20,
        });

        console.log('📥 12. Réponse backend →', data);

        if (data.code === 200 && data.data?.questions?.length >= 20) {
          console.log('🎉 SUCCÈS ! 20+ questions reçues');
          localStorage.setItem('testQuestions', JSON.stringify(data.data.questions));
          localStorage.setItem('testSessionId', String(data.data.sessionId));
          success('Test généré avec succès !');

          router.push({
            name: 'TestPage',
            query: {
              supportDeCoursId: formData.supportDeCours,
              moduleId: formData.module,
              sessionId: data.data.sessionId,
              mode: 'new',
            },
          });
        } else {
          console.warn('⚠️ Pas assez de questions →', data);
          error('Moins de 20 questions valides');
        }
      } catch (err: any) {
        console.error('💥 ERREUR FATALE /random-questions →', {
          status: err.response?.status,
          message: err.response?.data?.message,
          full: err.response?.data,
        });
        error(err.response?.data?.message || 'Échec génération test QCM');
      }
    };

    // ==================== MONTAGE DE LA PAGE ====================
    onMounted(() => {
      console.log('🌟 Page FaireTestQCM montée → lancement loadSupports()');
      loadSupports();
    });

    return {
      formData,
      supportOptions,
      moduleOptions,
      isLoadingSupports,
      isLoadingModules,
      testSchema,
      isDisableGenerate,
      onSupportChange,
      generateRandomTest,
      supportOptionsComputed
    };
  },
});
</script>

<style scoped>
.cursor-not-allowed {
  cursor: not-allowed;
}
</style>